import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {LayoutPortal} from '../../LayoutPortal';
import {usePortalClientId} from '../../hooks/usePortalClientId';
import {usePortalModules} from '../../hooks/usePortalModules';
import {usePreviewContext} from '../../context/PreviewContext';
import {portalHomeRedirectTarget, REDIRECT_CONNECT, REDIRECT_CONNECTIONS, REDIRECT_MEMORY, REDIRECT_REPORTS} from './portalHomeRedirect';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {ChannelLogo} from '../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL} from '../channels/channels';
import {ReportBlocks} from '../public_reports/ReportBlocks';
import {BUCampaignsDetail} from './PortalBudgets';
import {PortalDashboardTableV2} from './PortalDashboardTableV2';
import {
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN,
    GOAL_1_COLUMN, GOAL_2_COLUMN,
    CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN,
    CURRENT_PERIOD_COST_COLUMN,
} from '../dashboard/columns';
import {
    LEVEL_COLORS, getTodayAlert, IndicatorBadge, BudgetProgressBar, CHANNEL_BOOLEAN_MAP,
} from './portalUtils';

// --- Goal Progress Card ---
const GoalProgressCard = ({goalCol, t}) => {
    const alert = getTodayAlert(goalCol);
    if (!alert) return null;
    const level = alert.level || 'normal';
    const color = LEVEL_COLORS[level] || LEVEL_COLORS.normal;
    const pct = goalCol.goal_value
        ? Math.min(100, Math.round(Math.abs(alert.value / goalCol.goal_value) * 100))
        : 0;

    return (
        <div className="portal-bu-goal-card" style={{'--goal-color': color}}>
            <div className="portal-bu-goal-card-accent"/>
            <div className="portal-bu-goal-header">
                <span className="portal-bu-goal-metric-name">{goalCol.metric_name}</span>
                <IndicatorBadge alert={alert}/>
            </div>
            <div className="portal-bu-goal-values">
                <span className="portal-bu-goal-current">{alert.formatted_value || '—'}</span>
                <span className="portal-bu-goal-target">
                    {t('portal.home.goal_of')} {alert.formatted_goal_value || '—'}
                </span>
            </div>
            <div className="portal-bu-goal-progress">
                <div className="portal-bu-goal-progress-track">
                    <div
                        className="portal-bu-goal-progress-fill"
                        style={{width: `${Math.min(pct, 100)}%`, backgroundColor: color}}
                    />
                </div>
                <span className="portal-bu-goal-progress-label">{pct}%</span>
            </div>
            {goalCol.remaining_days_of_current_period != null && (
                <span className="portal-bu-goal-remaining">
                    {t('portal.home.remaining_days', {count: goalCol.remaining_days_of_current_period})}
                </span>
            )}
        </div>
    );
};

const PortalBUHeroCard = ({bu, t}) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showConfig, setShowConfig] = useState(false);
    const [syncInfo, setSyncInfo] = useState(null);
    const [showOldData, setShowOldData] = useState(false);

    useEffect(() => {
        new APIClient(API_URL + 'business-unit/' + bu.id + '/dashboard/metrics').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setMetrics(data.data);
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [bu.id]);

    useEffect(() => {
        new APIClient(API_URL + 'business-unit/' + bu.id + '/sync-information').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setSyncInfo(data.data);
            }
        }).catch(() => {});
    }, [bu.id]);

    const isCritical = syncInfo?.has_sync_problems_for_a_long_time && !showOldData;
    const isWarning = syncInfo?.has_sync_problems && !syncInfo?.has_sync_problems_for_a_long_time;

    // Build channels from boolean flags
    let channels = bu.channels;
    if (!Array.isArray(channels)) {
        channels = CHANNEL_BOOLEAN_MAP
            .filter(({field}) => bu[field])
            .map(({channel}) => channel);
    }

    // Extract indicator data
    const budgetCol = metrics?.[FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN];
    const budgetAlert = getTodayAlert(budgetCol);
    const budgetLevel = budgetAlert?.level || 'normal';
    const statusColor = LEVEL_COLORS[budgetLevel] || LEVEL_COLORS.normal;

    const allocatedBudgetCol = metrics?.[CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN];
    const costCol = metrics?.[CURRENT_PERIOD_COST_COLUMN];

    const goal1Col = metrics?.[GOAL_1_COLUMN];
    const hasGoal1 = goal1Col?.has_goal;

    const goal2Col = metrics?.[GOAL_2_COLUMN];
    const hasGoal2 = goal2Col?.has_goal;

    // Parse numeric values for progress bar
    const costNum = costCol?.value ?? 0;
    const budgetNum = allocatedBudgetCol?.value ?? 0;

    const periodKey = bu.period_type === 'EVENTUAL'
        ? 'portal.home.period_eventual'
        : 'portal.home.period_monthly';

    return (
        <div className="portal-bu-hero-card" style={{'--bu-status-color': statusColor}}>
            <div className="portal-bu-hero-card-accent" />
            <div className="portal-bu-hero-card-body">
                <div className="portal-bu-hero-card-header">
                    <div className="portal-bu-hero-card-header-left">
                        <h3 className="portal-bu-hero-card-name">{bu.name}</h3>
                        <span className="portal-bu-period-badge">{t(periodKey)}</span>
                    </div>
                    <div className="portal-bu-hero-card-channels">
                        {channels.map(ch => {
                            const info = CHANNEL_INFO_BY_CHANNEL[ch];
                            return info ? (
                                <span key={ch} className="portal-bu-hero-channel" title={info.Spanish}>
                                    <ChannelLogo channel={ch} height={16} width={16} hspace={0} vspace={0}/>
                                </span>
                            ) : null;
                        })}
                    </div>
                </div>

                {loading ? (
                    <div className="portal-bu-hero-card-loading">
                        <Spinner color="positive" size="sm"/>
                        <span>{t('portal.home.loading_indicators')}</span>
                    </div>
                ) : !metrics ? (
                    <div className="portal-bu-hero-card-empty">
                        {t('portal.home.no_indicator_data')}
                    </div>
                ) : isCritical ? (
                    <div className="portal-bu-sync-critical">
                        <FaIcon iconName="triangle-exclamation"/>
                        <p>{t('portal.home.sync_critical_message')}</p>
                        <button
                            className="portal-bu-sync-critical-btn"
                            onClick={() => setShowOldData(true)}
                        >
                            {t('portal.home.sync_show_data_anyway')}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="portal-bu-hero-card-metrics">
                            <div className="portal-bu-hero-metric">
                                <span className="portal-bu-hero-metric-label">{t('portal.home.cost')}</span>
                                <div className="portal-bu-hero-metric-row">
                                    <span className="portal-bu-hero-metric-value">
                                        {costCol?.formatted_value || '—'}
                                    </span>
                                    <IndicatorBadge alert={budgetAlert}/>
                                </div>
                            </div>
                            <div className="portal-bu-hero-metric">
                                <span className="portal-bu-hero-metric-label">{t('portal.home.budget_deviation')}</span>
                                <div className="portal-bu-hero-metric-row">
                                    <span className="portal-bu-hero-metric-value">
                                        {allocatedBudgetCol?.formatted_value || '—'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <BudgetProgressBar cost={costNum} budget={budgetNum}/>

                        {(hasGoal1 || hasGoal2) && (
                            <div className="portal-bu-hero-card-goals">
                                {hasGoal1 && <GoalProgressCard goalCol={goal1Col} t={t}/>}
                                {hasGoal2 && <GoalProgressCard goalCol={goal2Col} t={t}/>}
                            </div>
                        )}

                        {syncInfo?.last_sync && (
                            <div className={`portal-bu-sync-info${isWarning ? ' -warning' : ''}`}>
                                {t('portal.home.sync_warning_last_sync', {date: syncInfo.last_sync})}
                            </div>
                        )}
                    </>
                )}

                {/* Config toggle */}
                <div className="portal-bu-hero-config-toggle">
                    <button
                        className="portal-bu-hero-config-btn"
                        onClick={() => setShowConfig(!showConfig)}
                    >
                        <FaIcon iconName={showConfig ? 'chevron-up' : 'chevron-down'}/>
                        {showConfig ? t('portal.home.hide_detail') : t('portal.home.show_detail')}
                    </button>
                </div>

                {showConfig && (
                    <div className="portal-bu-hero-config-section">
                        <BUCampaignsDetail buId={bu.id} t={t}/>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Inline Portal Report ---
const PortalInlineReport = ({clientId, t}) => {
    const [report, setReport] = useState(null);
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const pollingRef = useRef(null);

    const fetchPortalReport = useCallback(() => {
        new APIClient(API_URL + `client/${clientId}/portal/report/`).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const reportData = data.data.report;
                const templateData = data.data.template;
                setTemplate(templateData);

                if (reportData) {
                    // Fetch full report blocks
                    new APIClient(API_URL + `reports/${reportData.report_id}/`).call('GET').then((detail) => {
                        if (detail && detail.status === 'OK') {
                            setReport(detail.data);
                            setGenerating(false);
                            // Si obtuvimos reporte, dejar de pollear
                            if (pollingRef.current) {
                                clearInterval(pollingRef.current);
                                pollingRef.current = null;
                            }
                        }
                        setLoading(false);
                    }).catch(() => setLoading(false));
                } else if (templateData) {
                    // Template asignado pero sin reporte: generar uno
                    setReport(null);
                    setLoading(false);
                    if (!generating) {
                        setGenerating(true);
                        triggerGeneration();
                    }
                } else {
                    setReport(null);
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        }).catch(() => setLoading(false));
    }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerGeneration = () => {
        new APIClient(API_URL + `client/${clientId}/portal/report/generate/`).call('POST')
            .then(() => {
                fetchPortalReport();
            }).catch(() => {
                setGenerating(false);
            });
    };

    useEffect(() => {
        fetchPortalReport();
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [fetchPortalReport]);

    // Auto-regenerate if report is stale (>4h)
    useEffect(() => {
        if (report && template) {
            const reportAge = report.generated_at
                ? (Date.now() - new Date(report.generated_at).getTime()) / (1000 * 60 * 60)
                : 999;
            if (reportAge > 4 && !generating) {
                setGenerating(true);
                triggerGeneration();
            }
        }
    }, [report, template]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="portal-report-section">
                <div className="portal-report-loading">
                    <Spinner color="positive" size="sm"/>
                </div>
            </div>
        );
    }

    // No template assigned
    if (!template) {
        return (
            <div className="portal-report-section">
                <div className="portal-report-empty">
                    <FaIcon iconName="file-chart-line" className="portal-report-empty-icon"/>
                    <p>{t('portal.home.no_report_configured')}</p>
                </div>
            </div>
        );
    }

    // Template assigned but report generating
    if (!report && generating) {
        return (
            <div className="portal-report-section">
                <div className="portal-report-header">
                    <h2 className="portal-report-title">{t('portal.home.report_title')}</h2>
                </div>
                <div className="portal-report-generating">
                    <Spinner color="positive" size="sm"/>
                    <p>{t('portal.home.report_generating')}</p>
                </div>
            </div>
        );
    }

    // Template assigned but no report and not generating (edge case)
    if (!report) {
        return (
            <div className="portal-report-section">
                <div className="portal-report-empty">
                    <FaIcon iconName="file-chart-line" className="portal-report-empty-icon"/>
                    <p>{t('portal.home.no_report_configured')}</p>
                </div>
            </div>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-AR', {day: 'numeric', month: 'short'});
    };

    const formatRangeDate = (dateStr) => {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-');
        const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        return date.toLocaleDateString('es-AR', {day: 'numeric', month: 'short', year: 'numeric'});
    };

    const reportAge = report.generated_at
        ? (Date.now() - new Date(report.generated_at).getTime()) / (1000 * 60 * 60)
        : 999;

    const dateRanges = report.metadata?.date_ranges;

    return (
        <div className="portal-report-section">
            <div className="portal-report-header">
                <div className="portal-report-header-left">
                    <h2 className="portal-report-title">{t('portal.home.report_title')}</h2>
                    {dateRanges && (
                        <span className="portal-report-period">
                            {formatRangeDate(dateRanges.start)} — {formatRangeDate(dateRanges.end)}
                        </span>
                    )}
                </div>
                <div className="portal-report-meta">
                    {generating && (
                        <span className="portal-report-refreshing">
                            <Spinner color="positive" size="sm"/>
                            {t('portal.home.report_refreshing')}
                        </span>
                    )}
                    {report.generated_at && (
                        <span className="portal-report-date">
                            {t('portal.home.report_updated')}: {formatDate(report.generated_at)}
                        </span>
                    )}
                    {!generating && reportAge > 0.5 && (
                        <button
                            className="portal-report-regenerate-btn"
                            title={t('portal.home.report_regenerate_tooltip')}
                            onClick={() => { setGenerating(true); triggerGeneration(); }}
                        >
                            <FaIcon iconName="sync-alt"/>
                        </button>
                    )}
                </div>
            </div>
            <div className="portal-report-content">
                <ReportBlocks
                    blocks={report.blocks || []}
                    reportId={report.report_id}
                    isAuthenticated={false}
                />
            </div>
        </div>
    );
};

// --- Main Component ---

export const PortalHome = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {isPreview, previewClientId} = usePreviewContext();
    const clientId = usePortalClientId();
    const modules = usePortalModules();
    const basePath = isPreview ? `/client/${previewClientId}/preview` : '/portal';

    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Sin Tablero: Connect → Informes (módulo REPORTS, el del nav) → Conexiones. La decisión
    // vive en `portalHomeRedirect.js` (pura, con tests).
    const redirectTarget = portalHomeRedirectTarget(modules);
    const shouldRedirectToConnect = redirectTarget === REDIRECT_CONNECT;
    const shouldRedirectToReports = redirectTarget === REDIRECT_REPORTS;
    const shouldRedirectToConnections = redirectTarget === REDIRECT_CONNECTIONS;
    const shouldRedirectToMemory = redirectTarget === REDIRECT_MEMORY;

    useEffect(() => {
        if (shouldRedirectToConnect) {
            navigate(`${basePath}/dashbo-mcp`, {replace: true});
        } else if (shouldRedirectToReports) {
            navigate(`${basePath}/reports`, {replace: true});
        } else if (shouldRedirectToMemory) {
            navigate(`${basePath}/memory`, {replace: true});
        } else if (shouldRedirectToConnections) {
            navigate(`${basePath}/connections`, {replace: true});
        }
    }, [shouldRedirectToConnect, shouldRedirectToReports, shouldRedirectToMemory, shouldRedirectToConnections, navigate, basePath]);

    // Fetch client data
    useEffect(() => {
        if (!clientId) return;
        new APIClient(API_URL + 'client/' + clientId + '?portal=true').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setClientData(data.data);
            }
            setLoading(false);
        });
    }, [clientId]);

    const businessUnits = clientData ? (clientData.business_units || []) : [];
    const activeBUs = businessUnits.filter(bu => bu.period_type !== 'EVENTUAL' || bu.active);

    // Modalidad del Tablero del Portal. El default es TABLE_V2 (Tablero): el campo
    // viene del presenter `AgencyClientData` con default backend TABLE_V2; el `||`
    // solo cubre el caso defensivo de que falte. Si la API V2 reporta que no hay
    // plantillas o el user tiene multi-client, el componente V2 invoca
    // `handleFallbackToCards` y volvemos al render de cards.
    const portalDashboardMode = clientData?.portal_dashboard_mode || 'TABLE_V2';
    const [forceCardsFallback, setForceCardsFallback] = useState(false);
    const showTableV2 = portalDashboardMode === 'TABLE_V2' && !forceCardsFallback;
    const handleFallbackToCards = useCallback(() => setForceCardsFallback(true), []);

    return (
        <LayoutPortal>
            <div className="portal-home">
                {loading ? (
                    <div className="portal-loading">
                        <Spinner color="positive"/>
                    </div>
                ) : clientData ? (
                    <>
                        {/* Tablero formato tabla (V2) o cards (V1) según modo del cliente */}
                        {showTableV2 ? (
                            <>
                                <h2 className="portal-section-title">{t('portal.home.control_lines')}</h2>
                                <PortalDashboardTableV2
                                    businessUnits={activeBUs}
                                    onFallbackToCards={handleFallbackToCards}
                                />
                            </>
                        ) : (
                            activeBUs.length > 0 && (
                                <>
                                    <h2 className="portal-section-title">{t('portal.home.control_lines')}</h2>
                                    <div className="portal-bu-hero-cards">
                                        {activeBUs.map(bu => (
                                            <PortalBUHeroCard key={bu.id} bu={bu} t={t}/>
                                        ))}
                                    </div>
                                </>
                            )
                        )}

                        {/* Inline Report from Template — sólo en CARDS_V1.
                            En TABLE_V2 el cliente ya tiene la tabla completa con
                            todas sus métricas, mostrar también el Report inline
                            (que para muchas agencias es un empty state) es ruido. */}
                        {!showTableV2 && <PortalInlineReport clientId={clientId} t={t}/>}
                    </>
                ) : null}
            </div>
        </LayoutPortal>
    );
};
